import { PortfolioFieldsFragment_ } from './__generated__/graphql';

enum ScoreSectionsEnum {
  revenue = 'revenue',
  capex = 'capex',
  opex = 'opex',
}

type Proportion = {
  revenue: number;
  capex: number;
  opex: number;
};

type SubstantialContributionProportion = Record<EnvironmentalObjectiveKeyEnum, Proportion>;

enum EligibilityStatus {
  notSure = 'NOT_SURE',
  notEligible = 'NOT_ELIGIBLE',
  eligible = 'ELIGIBLE',
}

const zeroScores: SharedTaxonomyScore = {
  aligned: 0,
  eligible: 0,
  inProgress: 0,
  enabling: 0,
  transitional: 0,
  total: 100,
};

export type PortfolioCompany = PortfolioFieldsFragment_['portfolioCompanies'][number] & {
  scores?: PortfolioCompanyScore['scores'];
  progress?: PortfolioCompanyScore['progress'];
  substantialContributionProportion?: SubstantialContributionProportion;
};

export type Portfolio = Omit<PortfolioFieldsFragment_, 'portfolioCompanies'> & {
  portfolioCompanies: PortfolioCompany[];
};

type TaxonomyScore = {
  total: number;
  eligible: number;
  aligned: number;
  inProgress?: number;
  enabling?: number;
  transitional?: number;
};

export type SharedTaxonomyScore = {
  total?: number | null;
  aligned?: number | null;
  inProgress?: number | null;
  eligible?: number | null;
  enabling?: number | null;
  transitional?: number | null;
};

export type PortfolioCompanyScore = {
  scores: {
    capex: SharedTaxonomyScore;
    opex: SharedTaxonomyScore;
    revenue: SharedTaxonomyScore;
  };
  progress: {
    financials: number;
    screening: number;
    isLocked: boolean;
  };
};

export enum ReportPeriods {
  'q1' = 'q1',
  'q2' = 'q2',
  'q3' = 'q3',
  'q4' = 'q4',
  'year' = 'year',
}

export type CashSovereignsOptions = {
  cash: number;
  sovereigns: number;
};

export enum EnvironmentalObjectiveKeyEnum {
  mitigation = 'mitigation',
  adaptation = 'adaptation',
  biodiversity = 'biodiversity',
  circular = 'circular',
  pollution = 'pollution',
  water = 'water',
}

export const scoreSections = Object.values(ScoreSectionsEnum);

const transformEstimateScoreToNumerics = (scores: {
  [key: string]: { aligned: string; eligible: string; inProgress: string };
}) => {
  return Object.keys(scores).reduce((acc, key) => {
    return {
      ...acc,
      [key]: {
        aligned: Number(scores[key].aligned),
        eligible: Number(scores[key].eligible),
        inProgress: Number(scores[key].inProgress),
        total: 100,
      },
    };
  }, {});
};

const emptySCProportions: SubstantialContributionProportion = {
  adaptation: { revenue: 0, capex: 0, opex: 0 },
  mitigation: { revenue: 0, capex: 0, opex: 0 },
  biodiversity: { revenue: 0, capex: 0, opex: 0 },
  pollution: { revenue: 0, capex: 0, opex: 0 },
  circular: { revenue: 0, capex: 0, opex: 0 },
  water: { revenue: 0, capex: 0, opex: 0 },
};

export const formatQuarterYear = (quarter: string, year?: string | number | null) => {
  return `${quarter}-${year ?? new Date().getUTCFullYear()}`.toUpperCase();
};

const transformPortfolioCompany = (
  portfolioCompany: Portfolio['portfolioCompanies'][0],
  period: string | null,
  cashAndSovereigns?: CashSovereignsOptions
) => {
  if (portfolioCompany.company?.isPortfolioOwner) {
    const portfolios = portfolioCompany.company?.portfolios ?? [];
    const portfoliosInYear = period
      ? portfolios.filter((portfolio) =>
          portfolio.portfolioCompanies.some((c) => formatQuarterYear(c.quarter, c.year) === period)
        )
      : portfolios;
    const portfolioCompanies: PortfolioCompany[] = portfolioCompany.company?.portfolios
      ?.flatMap((p) =>
        p.portfolioCompanies
          .map((pc) => ({
            ...pc,
            id: pc.company?.id ?? 'defaultId',
            company: {
              ...pc.company,
              currency: pc.company?.currency ?? 'NOK',
              name: pc.company?.name ?? 'defaultName',
              id: pc.company?.id ?? 'defaultId',
              isPortfolioOwner: false,
              portfolios: [],
            },
          }))
          .filter((pc) => (period ? formatQuarterYear(pc.quarter, pc.year) === period : true))
      )
      .filter((pc) => !!pc)
      .map((portCo) => transformPortfolioCompany(portCo, period, cashAndSovereigns));

    const score =
      portfoliosInYear.length > 0
        ? getAggregatePortfolioScores(
            portfolioCompanies ?? [],
            ReportPeriods.year,
            cashAndSovereigns
          )
        : {
            capex: zeroScores,
            opex: zeroScores,
            revenue: zeroScores,
          };
    const progress =
      portfoliosInYear.length > 0
        ? aggregatePortfolioProgress(portfolioCompanies ?? [], ReportPeriods.year)
        : {
            screening: 0,
            financials: 0,
            isLocked: false,
          };
    const substantialContributionProportion =
      portfoliosInYear.length > 0
        ? getAggregatePortfolioSubstantialContribution(portfolioCompanies ?? [], ReportPeriods.year)
        : emptySCProportions;

    return {
      ...portfolioCompany,
      name: portfolioCompany?.company?.name,
      score,
      scores: score, // Need consistent naming between scores/score, one of them to be deprecated
      progress,
      substantialContributionProportion,
      year: portfolioCompany?.year,
    };
  }
  const isEstimate = !portfolioCompany?.company;
  const eligibilityStatus = portfolioCompany?.eligibilityStatus;
  const progress: PortfolioCompanyScore['progress'] =
    isEstimate || eligibilityStatus === EligibilityStatus.notEligible
      ? { screening: 100, financials: 100, isLocked: false }
      : {
          screening: portfolioCompany.sharedAssessment?.cachedResult?.progress?.screening ?? 0,
          financials: portfolioCompany.sharedAssessment?.cachedResult?.progress?.financials ?? 0,
          isLocked: portfolioCompany.sharedAssessment?.isLocked || false,
        };

  const score: PortfolioCompanyScore['scores'] = isEstimate
    ? (transformEstimateScoreToNumerics(portfolioCompany?.estimateCompany?.scores) ?? {})
    : eligibilityStatus === EligibilityStatus.notEligible
      ? { capex: zeroScores, opex: zeroScores, revenue: zeroScores }
      : (portfolioCompany.sharedAssessment?.cachedResult?.score ?? {});

  const substantialContributionProportion: SubstantialContributionProportion = isEstimate
    ? emptySCProportions
    : (portfolioCompany.sharedAssessment?.cachedResult?.objectivesState
        ?.substantialContributionProportion ?? emptySCProportions);

  return {
    ...portfolioCompany,
    name: portfolioCompany?.company?.name,
    score,
    scores: score, // Need consistent naming between scores/score, one of them to be deprecated
    progress,
    substantialContributionProportion,
    year: portfolioCompany?.year,
  };
};

export const transformPortfolios = (portfolios: Portfolio[], year: string | null) => {
  const portfoliosInYear = year
    ? portfolios.filter((portfolio) =>
        portfolio.portfolioCompanies.some((c) => formatQuarterYear(c.quarter, c.year) === year)
      )
    : portfolios;
  return portfoliosInYear.map((portfolio) => ({
    ...portfolio,
    portfolioCompanies: portfolio.portfolioCompanies
      .filter((c) => !!c.company?.id || !!c.estimateCompany?.id)
      .map((portCo) => transformPortfolioCompany(portCo, year)),
  }));
};

export const getInvestmentValue = (valueOfInvestments: any, reportPeriod: ReportPeriods) => {
  const quarterValue = valueOfInvestments?.[reportPeriod] ?? 0;
  const yearValue =
    (valueOfInvestments?.q1 +
      valueOfInvestments?.q2 +
      valueOfInvestments?.q3 +
      valueOfInvestments?.q4) /
    4;
  if (reportPeriod === ReportPeriods.year) {
    return yearValue ?? 0;
  }
  return quarterValue;
};

export const getPortfolioTotal = (
  portfolioCompanies: PortfolioCompany[],
  reportPeriod: ReportPeriods,
  cashAndSovereigns?: CashSovereignsOptions
) => {
  const cash = cashAndSovereigns?.cash ?? 0;
  const sovereigns = cashAndSovereigns?.sovereigns ?? 0;

  const total =
    portfolioCompanies.reduce(
      (sum, { valueOfInvestments }) =>
        sum + (getInvestmentValue(valueOfInvestments, reportPeriod) ?? 0),
      0
    ) +
    cash +
    sovereigns;

  return total;
};

export const getAggregatePortfolioScores = (
  portfolioCompanies: PortfolioCompany[],
  reportPeriod: ReportPeriods,
  cashAndSovereigns?: CashSovereignsOptions
) => {
  const total = getPortfolioTotal(portfolioCompanies, reportPeriod, cashAndSovereigns);

  const isAllInProgress = portfolioCompanies.every(
    (c) => (c.progress?.financials === 0 || c.progress?.screening === 0) && !c.progress?.isLocked
  );

  if (isAllInProgress) {
    return scoreSections.reduce(
      (acc, section) => {
        acc[section] = {
          inProgress: 100,
          total: 100,
          aligned: 0,
          eligible: 0,
          enabling: 0,
          transitional: 0,
        };
        return acc;
      },
      {} as Record<ScoreSectionsEnum, TaxonomyScore>
    );
  }

  const sectionScores = scoreSections.reduce(
    (scores, section) => {
      const aligned = portfolioCompanies.reduce((sum, pCompany) => {
        const investmentValue = getInvestmentValue(pCompany.valueOfInvestments, reportPeriod);

        const percentage = investmentValue / total;

        if (!pCompany?.company) {
          return sum + percentage * (pCompany?.estimateCompany?.scores?.[section]?.aligned ?? 0);
        }
        return sum + percentage * (pCompany?.scores?.[section]?.aligned ?? 0);
      }, 0);

      const eligible = portfolioCompanies.reduce((sum, pCompany) => {
        const investmentValue = getInvestmentValue(pCompany.valueOfInvestments, reportPeriod);
        const percentage = investmentValue / total;

        const isFullyInProgress =
          (pCompany?.progress?.financials === 0 || pCompany?.progress?.screening === 0) &&
          !pCompany?.progress?.isLocked;

        const isDoneAndNull =
          pCompany?.progress?.financials === 100 &&
          pCompany?.progress?.screening === 100 &&
          pCompany?.scores?.[section]?.inProgress === null;

        if (!pCompany?.company) {
          return sum + percentage * pCompany?.estimateCompany?.scores?.[section]?.eligible;
        }

        return (
          sum +
          percentage *
            (isFullyInProgress
              ? 100
              : isDoneAndNull
                ? 0
                : (pCompany?.scores?.[section]?.eligible ?? 100))
        );
      }, 0);

      const inProgress = portfolioCompanies.reduce((sum, pCompany) => {
        const investmentValue = getInvestmentValue(pCompany.valueOfInvestments, reportPeriod);

        const percentage = investmentValue / total;
        if (!pCompany?.company) {
          return sum;
        }
        const isFullyInProgress =
          (pCompany?.progress?.financials === 0 || pCompany?.progress?.screening === 0) &&
          !pCompany?.progress?.isLocked;
        const isDoneAndNull =
          pCompany?.progress?.financials === 100 &&
          pCompany?.progress?.screening === 100 &&
          pCompany?.scores?.[section]?.inProgress === null;

        return (
          sum +
          percentage *
            (isFullyInProgress
              ? 100
              : isDoneAndNull
                ? 0
                : (pCompany?.scores?.[section]?.inProgress ?? 100))
        );
      }, 0);

      const enabling = portfolioCompanies.reduce((sum, pCompany) => {
        const investmentValue = getInvestmentValue(pCompany.valueOfInvestments, reportPeriod);
        const percentage = investmentValue / total;

        return sum + percentage * (pCompany?.scores?.[section]?.enabling ?? 0);
      }, 0);

      const transitional = portfolioCompanies.reduce((sum, pCompany) => {
        const investmentValue = getInvestmentValue(pCompany.valueOfInvestments, reportPeriod);

        const percentage = investmentValue / total;

        return sum + percentage * (pCompany?.scores?.[section]?.transitional ?? 0);
      }, 0);

      scores[section] = {
        total: 100,
        aligned,
        eligible,
        inProgress,
        enabling,
        transitional,
      };
      return scores;
    },
    {} as Record<ScoreSectionsEnum, TaxonomyScore>
  );

  return sectionScores;
};

export const aggregatePortfolioProgress = (
  portfolioCompanies: PortfolioCompany[],
  reportPeriod: ReportPeriods,
  cashAndSovereigns?: CashSovereignsOptions
) => {
  const total = getPortfolioTotal(portfolioCompanies, reportPeriod, cashAndSovereigns);

  const portfolioProgress = portfolioCompanies.reduce(
    (agg, pCompany) => {
      const investmentValue = getInvestmentValue(pCompany.valueOfInvestments, reportPeriod);

      const percentage = investmentValue / total;
      const currentProgress = pCompany.progress ?? {
        screening: 0,
        financials: 0,
      };
      return {
        financials: agg.financials + percentage * currentProgress.financials,
        screening: agg.screening + percentage * currentProgress.screening,
        isLocked: (agg.isLocked && pCompany.sharedAssessment?.isLocked) || false,
      };
    },
    { financials: 0, screening: 0, isLocked: true }
  );
  return portfolioProgress;
};

export const getAggregatePortfolioSubstantialContribution = (
  portfolioCompanies: PortfolioCompany[],
  reportPeriod: ReportPeriods,
  cashAndSovereigns?: CashSovereignsOptions
) => {
  const total = getPortfolioTotal(portfolioCompanies, reportPeriod, cashAndSovereigns);

  const isAllInProgress = portfolioCompanies.every(
    (c) => c.progress?.financials === 0 || c.progress?.screening === 0
  );

  if (isAllInProgress) {
    return Object.values(EnvironmentalObjectiveKeyEnum).reduce(
      (acc, currentObjective) => {
        acc[currentObjective] = {
          revenue: 0,
          capex: 0,
          opex: 0,
        };
        return acc;
      },
      {} as Record<EnvironmentalObjectiveKeyEnum, { revenue: number; opex: number; capex: number }>
    );
  }

  const proportionsPerObjective = Object.values(EnvironmentalObjectiveKeyEnum).reduce(
    (acc, currentObjective) => {
      const sectionScores = scoreSections.reduce(
        (scores, section) => {
          const weighedSubstantialContributionPercentage = portfolioCompanies.reduce(
            (sum, pCompany) => {
              const investmentValue = getInvestmentValue(pCompany.valueOfInvestments, reportPeriod);
              const percentage = investmentValue / total;

              return (
                sum +
                percentage *
                  (pCompany?.substantialContributionProportion?.[currentObjective]?.[section] ?? 0)
              );
            },
            0
          );

          scores[section] = weighedSubstantialContributionPercentage;
          return scores;
        },
        {} as Record<ScoreSectionsEnum, number>
      );

      acc[currentObjective] = sectionScores;
      return acc;
    },
    {} as Record<EnvironmentalObjectiveKeyEnum, { revenue: number; opex: number; capex: number }>
  );

  return proportionsPerObjective;
};
